import type { DropdownSelectItem } from '@/components/lemmala-design-system/dropdown/dropdown-select.types'
import { indexItemTree } from '@/components/lemmala-design-system/dropdown/utils'

/** Lists each value under its parent in `childValues`. A value is listed once, under the first parent
 * reached; one whose parents are never reached, as in a cycle, is listed at the top. */
export const nestFilterValues = (
  values: string[],
  childValues: Record<string, string[]>,
): DropdownSelectItem[] => {
  const listedValues = new Set(values)
  const valuesUnderListedParent = new Set(
    Object.entries(childValues)
      .filter(([parent]) => listedValues.has(parent))
      .flatMap(([, children]) => children),
  )

  const placed = new Set<string>()
  const place = (value: string): DropdownSelectItem => {
    placed.add(value)
    const children: DropdownSelectItem[] = []
    for (const child of childValues[value] ?? []) {
      if (!placed.has(child)) children.push(place(child))
    }
    return { id: value, label: value, children: children.length > 0 ? children : undefined }
  }

  const items: DropdownSelectItem[] = []
  for (const value of values) {
    if (!placed.has(value) && !valuesUnderListedParent.has(value)) items.push(place(value))
  }
  for (const value of values) {
    if (!placed.has(value)) items.push(place(value))
  }
  return items
}

/** A checked parent already stands for its children, so they aren't named again. */
export const collapseSelectedValues = (
  selectedValues: string[],
  items: DropdownSelectItem[],
): string[] => {
  const { parentIdById } = indexItemTree(items)
  return selectedValues.filter((value) => {
    const parent = parentIdById.get(value)
    return !parent || !selectedValues.includes(parent)
  })
}
